import { useCallback, useMemo } from 'react';
import { EXHIBITOR_STEP_CONFIGS } from './steps/exhibitor-steps';
import {
    useHDERegistrationForm,
    type FilterVisibleStepsContext,
} from '../../hooks/useHDERegistrationForm';
import {
    filterVisibleProExhibitorSteps,
    createGetInitialDataForStep,
    createBuildSubmitPayload,
    getSelectedProfessionalUrlSegment,
    type HDEProfessionalProfileWithHubspot,
} from './shared-registration-logic';
import type { LocationOption } from './types';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';

interface ExhibitorRegistrationFormProps {
    locationOptions: LocationOption[];
    layout?: 'single' | 'split';
    onClose?: () => void;
    onStepChange?: (stepId: string) => void;
    onAuthViewChange?: (showEmailForm: boolean) => void;
    onAuthLoadingChange?: (loading: boolean) => void;
    socialLoginSubmitted?: boolean;
    socialLoginResumed?: boolean;
    socialLoginEventId?: string | null;
    onBackToApplicationType?: () => void;
}

interface FormData {
    isProfessionalMember: boolean | null;
    selectedProfessionalId: string | null;
    eventId: string | null;
    fullName: string;
    companyName: string;
    companyWebsite: string;
    companyEmail: string;
    phone: string;
    companyType: string | null;
    companyTypeOther: string;
    companyLocation: string;
    companySelectedLocation: DetailedPlace | null;
    companyShowOtherInput: boolean;
}

const INITIAL_FORM_DATA: FormData = {
    isProfessionalMember: null,
    selectedProfessionalId: null,
    eventId: null,
    fullName: '',
    companyName: '',
    companyWebsite: '',
    companyEmail: '',
    phone: '',
    companyType: null,
    companyTypeOther: '',
    companyLocation: '',
    companySelectedLocation: null,
    companyShowOtherInput: false,
};

export function ExhibitorRegistrationForm({
    locationOptions,
    layout = 'single',
    onClose,
    onStepChange,
    onAuthViewChange,
    onAuthLoadingChange,
    socialLoginSubmitted = false,
    socialLoginResumed = false,
    socialLoginEventId = null,
    onBackToApplicationType,
}: ExhibitorRegistrationFormProps) {
    const initialFormData = useMemo(
        () =>
            socialLoginResumed
                ? {
                      ...INITIAL_FORM_DATA,
                      isProfessionalMember: true as const,
                      eventId: socialLoginEventId ?? null,
                  }
                : INITIAL_FORM_DATA,
        [socialLoginResumed, socialLoginEventId],
    );
    const filterVisibleSteps = useCallback(
        (
            configs: typeof EXHIBITOR_STEP_CONFIGS,
            context: FilterVisibleStepsContext,
        ) => filterVisibleProExhibitorSteps(configs, context),
        [],
    );

    const getInitialDataForStep = useMemo(
        () =>
            createGetInitialDataForStep<FormData>({
                applicationType: 'Exhibitor',
                socialLoginEventId,
                getSuccessStepExtras: (formData, selectedProfile) => {
                    const selectedProfileWithHubspot =
                        selectedProfile as HDEProfessionalProfileWithHubspot | null;
                    const clientDirector =
                        selectedProfileWithHubspot?.HubspotCompany
                            ?.ClientDirectorTeamMember ?? null;

                    return {
                        companyName:
                            formData.companyName ||
                            selectedProfile?.Title ||
                            'your business',
                        clientDirector,
                        clientDirectorDisplayName:
                            clientDirector?.Name?.trim() ||
                            'Client Director to be assigned',
                    };
                },
            }),
        [socialLoginEventId],
    );

    const buildSubmitPayload = useMemo(
        () =>
            createBuildSubmitPayload<FormData>({
                applicationType: 'Exhibitor',
                locationOptions,
            }),
        [locationOptions],
    );

    const form = useHDERegistrationForm<FormData>({
        initialFormData,
        stepConfigs: EXHIBITOR_STEP_CONFIGS,
        layout,
        filterVisibleSteps,
        getInitialDataForStep,
        buildSubmitPayload,
        hasGuestInvitation: false,
        locationOptions,
        onClose,
        onStepChange,
        onAuthViewChange,
        onAuthLoadingChange,
        socialLoginSubmitted,
        onBackToApplicationType,
        indicatorStepOffset: 1,
        applicationType: 'Exhibitor',
        getSelectedProfessionalUrlSegment: (fd, u) =>
            getSelectedProfessionalUrlSegment(fd, u),
    });

    return form.renderForm();
}
